"use client";

import Image from "@/components/shared/image";
import { useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useAuth } from "@/components/auth/AuthProvider";
import { buttonVariants } from "@/components/ui/button";
import { useAnalytics } from "@/lib/analytics";
import { siteContent } from "@/lib/site-content";
import { cn } from "@/lib/utils";
import { useColorTheme } from "@/components/providers/color-theme-provider";
import {
  getHomepageMotionEnvironment,
  shouldUseHeavyHomepageMotion,
} from "@/components/LandingPage/mobile-motion-policy";

/** Ring order: top row L→R, middle L + hub + R, bottom L→R (matches siteContent.features.items index 0–7). */
const tint = [
  "bg-violet-200 text-violet-950 dark:bg-violet-950 dark:text-violet-100",
  "bg-pink-200 text-pink-950 dark:bg-pink-950 dark:text-pink-100",
  "bg-amber-200 text-amber-950 dark:bg-amber-950 dark:text-amber-100",
  "bg-emerald-200 text-emerald-950 dark:bg-emerald-950 dark:text-emerald-100",
  "bg-sky-200 text-sky-950 dark:bg-sky-950 dark:text-sky-100",
  "bg-orange-200 text-orange-950 dark:bg-orange-950 dark:text-orange-100",
  "bg-lime-200 text-lime-950 dark:bg-lime-950 dark:text-lime-100",
  "bg-fuchsia-200 text-fuchsia-950 dark:bg-fuchsia-950 dark:text-fuchsia-100",
] as const;

function SectionHeading() {
  const raw = siteContent.features.title;
  const dot = raw.indexOf(". ");
  const a = dot >= 0 ? raw.slice(0, dot + 1).trim() : raw;
  const b = dot >= 0 ? raw.slice(dot + 2).trim().replace(/\.$/, "") : "";
  return (
    <div className="mx-auto mb-6 max-w-3xl text-center sm:mb-8">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground sm:text-sm">
        Capabilities
      </p>
      <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
        <span className="gradient-text">{a}</span>
        {b ? (
          <>
            <br />
            <span className="text-foreground">{b}.</span>
          </>
        ) : null}
      </h2>
      <p className="mt-3 text-base text-muted-foreground sm:text-lg">{siteContent.features.subtitle}</p>
    </div>
  );
}

function HubCenter() {
  const { openAuth } = useAuth();
  const { track } = useAnalytics();
  const { themeColor } = useColorTheme();
  return (
    <div
      data-feature-cell
      className={cn(
        "relative z-20 m-auto flex aspect-square w-[calc(100%-10px)] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-background p-5 sm:p-6 lg:p-8",
      )}
    >
      <span className="text-xs font-semibold uppercase tracking-[0.35em] text-muted-foreground sm:text-sm">
        Zapify
      </span>
      <p className="mt-3 text-center text-4xl font-black leading-none tracking-tighter sm:text-5xl lg:text-6xl xl:text-7xl">
        <span className="gradient-text">FEATURES</span>
      </p>
      <button
        type="button"
        onClick={() => {
          track("cta_clicked", {
            cta_id: "CTA_Features",
            button_text: siteContent.hero.ctaPrimary,
          });
          openAuth();
        }}
        className={cn(
          buttonVariants({ size: "lg", variant: "default" }),
          "pointer-events-auto mt-3 font-bold sm:mt-5 !h-11 !rounded-full !px-8 !text-sm sm:!h-12 sm:!px-10 sm:!text-base dark:!text-black",
          themeColor === "yellow" && "!text-black"
        )}
      >
        {siteContent.hero.ctaPrimary}
      </button>
    </div>
  );
}

function HubTile({
  title,
  lines,
  imageLight,
  imageDark,
  index,
}: {
  title: string;
  lines: readonly [string, string];
  imageLight: string;
  imageDark: string;
  index: number;
}) {
  const tone = tint[index % tint.length]!;
  const tilt = index % 2 === 0 ? "-rotate-[2.5deg]" : "rotate-[2.5deg]";
  const num = String(index + 1).padStart(2, "0");
  const [line1, line2] = lines;

  return (
    <article
      data-feature-cell
      data-feature-tile
      className={cn(
        "relative flex min-h-0 flex-col overflow-hidden rounded-lg border border-border/60 p-3 pt-10 sm:p-3.5 sm:pt-11 lg:p-4 lg:pt-12 dark:border-border",
        tone,
      )}
    >
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute right-2 top-2 z-[5] origin-top-right font-mono text-base font-bold tabular-nums tracking-tighter text-foreground/30 sm:right-2.5 sm:top-2.5 sm:text-lg lg:right-3 lg:top-3 lg:text-xl",
          index % 2 === 0 ? "rotate-[2deg]" : "-rotate-[2deg]",
        )}
      >
        {num}
      </span>

      <div
        className={cn(
          "pointer-events-none absolute left-2 top-2 z-10 origin-top-left sm:left-2.5 sm:top-2.5 lg:left-3 lg:top-3",
          tilt,
        )}
      >
        <div data-feature-badge className="will-change-transform">
          <h3
            className={cn(
              "max-w-[92%] rounded-md bg-background px-2 py-1 text-[10px] font-bold leading-tight tracking-tight shadow-sm ring-1 ring-black/5 dark:bg-background dark:ring-white/10 sm:max-w-[11rem] sm:px-2.5 sm:py-1.5 sm:text-[11px] lg:text-xs",
            )}
          >
            {title}
          </h3>
        </div>
      </div>

      <div
        data-feature-visual
        className="relative mx-auto aspect-square w-full max-w-[min(100%,220px)] will-change-transform sm:max-w-[min(100%,260px)] lg:max-w-[min(100%,300px)]"
      >
        <div className="relative size-full overflow-hidden rounded-lg bg-white dark:bg-black">
          <Image
            src={imageLight}
            alt={`${title}: ${line1} ${line2}`}
            fill
            sizes="(max-width: 640px) 33vw, (max-width: 1024px) 28vw, 300px"
            className="rounded-lg object-contain p-2 sm:p-2.5 dark:hidden"
            srcSet={`${imageLight.replace(/\.webp$/, '_300w.webp')} 300w, ${imageLight} 600w`}
          />
          <Image
            src={imageDark}
            alt=""
            fill
            sizes="(max-width: 640px) 33vw, (max-width: 1024px) 28vw, 300px"
            className="hidden rounded-lg object-contain p-2 sm:p-2.5 dark:block"
            aria-hidden
            srcSet={`${imageDark.replace(/\.webp$/, '_300w.webp')} 300w, ${imageDark} 600w`}
          />
        </div>
      </div>

      <div className="mt-2.5 space-y-1 text-center sm:mt-3">
        <p className="mx-auto max-w-prose text-balance text-[10px] leading-snug text-foreground/90 sm:text-[11px] lg:text-xs">
          {line1}
        </p>
        <p className="mx-auto max-w-prose text-balance text-[10px] leading-snug text-foreground/90 sm:text-[11px] lg:text-xs">
          {line2}
        </p>
      </div>
    </article>
  );
}

export default function Features() {
  const items = siteContent.features.items;
  const gridRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!shouldUseHeavyHomepageMotion(getHomepageMotionEnvironment())) return;

    gsap.registerPlugin(ScrollTrigger);

    const cleanups: (() => void)[] = [];

    // ── Desktop: individual card entrance per column ───────────────
    const grid = gridRef.current;
    if (grid) {
      const leftCols   = Array.from(grid.querySelectorAll<HTMLElement>("[data-col='left']"));
      const centerCols = Array.from(grid.querySelectorAll<HTMLElement>("[data-col='center']"));
      const rightCols  = Array.from(grid.querySelectorAll<HTMLElement>("[data-col='right']"));

      leftCols.forEach((card) => {
        gsap.set(card, { x: -80, opacity: 0, rotation: -6 });
        const st = ScrollTrigger.create({
          trigger: card, start: "top 88%", once: true,
          onEnter: () => gsap.to(card, { x: 0, opacity: 1, rotation: 0, duration: 1.1, ease: "power3.out" }),
        });
        cleanups.push(() => st.kill());
      });

      centerCols.forEach((card) => {
        gsap.set(card, { y: 80, opacity: 0 });
        const st = ScrollTrigger.create({
          trigger: card, start: "top 88%", once: true,
          onEnter: () => gsap.to(card, { y: 0, opacity: 1, duration: 1.1, ease: "power3.out" }),
        });
        cleanups.push(() => st.kill());
      });

      rightCols.forEach((card) => {
        gsap.set(card, { x: 80, opacity: 0, rotation: 6 });
        const st = ScrollTrigger.create({
          trigger: card, start: "top 88%", once: true,
          onEnter: () => gsap.to(card, { x: 0, opacity: 1, rotation: 0, duration: 1.1, ease: "power3.out" }),
        });
        cleanups.push(() => st.kill());
      });
    }

    // ── Mobile: alternating left/right with tilt ───────────────────
    const mobileGrid = mobileRef.current;
    if (mobileGrid) {
      const cards = Array.from(mobileGrid.querySelectorAll<HTMLElement>("[data-mobile-card]"));

      cards.forEach((card, i) => {
        const fromX  = i % 2 === 0 ? -80 : 80;
        const fromRot = i % 2 === 0 ? -8 : 8;
        gsap.set(card, { x: fromX, opacity: 0, rotation: fromRot });

        const st = ScrollTrigger.create({
          trigger: card,
          start: "top 90%",
          once: true,
          onEnter: () => {
            gsap.to(card, { x: 0, opacity: 1, rotation: 0, duration: 1.0, ease: "power3.out" });
          },
        });
        cleanups.push(() => st.kill());
      });
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return (
    <section className="w-full border-t border-border/40 bg-white dark:bg-background" id="features">
      <div className="mx-auto w-full max-w-7xl container-padding section-y xl:max-w-[90rem]">
        <SectionHeading />

        <div className="relative w-full rounded-md border border-border/60 bg-muted/15 p-3 sm:p-4 lg:p-6">
          <div ref={mobileRef} className="space-y-3 sm:hidden">
            {/* First 4 cards */}
            {items.slice(0, 4).map((item, i) => (
              <div key={`mobile-${item.title}`} data-mobile-card>
                <HubTile
                  title={item.title}
                  lines={item.lines}
                  imageLight={item.imageLight}
                  imageDark={item.imageDark}
                  index={i}
                />
              </div>
            ))}
            {/* CTA card centered */}
            <div data-mobile-card><HubCenter /></div>
            {/* Last 4 cards */}
            {items.slice(4).map((item, i) => (
              <div key={`mobile-${item.title}`} data-mobile-card>
                <HubTile
                  title={item.title}
                  lines={item.lines}
                  imageLight={item.imageLight}
                  imageDark={item.imageDark}
                  index={i + 4}
                />
              </div>
            ))}
          </div>

          <div ref={gridRef} className="relative hidden w-full grid-cols-3 gap-2 sm:grid sm:gap-3 lg:gap-4">
            <div data-col="left"><HubTile title={items[0]!.title} lines={items[0]!.lines} imageLight={items[0]!.imageLight} imageDark={items[0]!.imageDark} index={0} /></div>
            <div data-col="center"><HubTile title={items[1]!.title} lines={items[1]!.lines} imageLight={items[1]!.imageLight} imageDark={items[1]!.imageDark} index={1} /></div>
            <div data-col="right"><HubTile title={items[2]!.title} lines={items[2]!.lines} imageLight={items[2]!.imageLight} imageDark={items[2]!.imageDark} index={2} /></div>

            <div data-col="left"><HubTile title={items[3]!.title} lines={items[3]!.lines} imageLight={items[3]!.imageLight} imageDark={items[3]!.imageDark} index={3} /></div>
            <div data-col="center"><HubCenter /></div>
            <div data-col="right"><HubTile title={items[4]!.title} lines={items[4]!.lines} imageLight={items[4]!.imageLight} imageDark={items[4]!.imageDark} index={4} /></div>

            <div data-col="left"><HubTile title={items[5]!.title} lines={items[5]!.lines} imageLight={items[5]!.imageLight} imageDark={items[5]!.imageDark} index={5} /></div>
            <div data-col="center"><HubTile title={items[6]!.title} lines={items[6]!.lines} imageLight={items[6]!.imageLight} imageDark={items[6]!.imageDark} index={6} /></div>
            <div data-col="right"><HubTile title={items[7]!.title} lines={items[7]!.lines} imageLight={items[7]!.imageLight} imageDark={items[7]!.imageDark} index={7} /></div>
          </div>
        </div>
      </div>
    </section>
  );
}
